import { createUseStyles } from 'react-jss';
import { Colors, Theme } from '@app/theme';
import mapValues from 'lodash/mapValues';
import { Trans } from 'react-i18next';
import classnames from 'classnames';
import { IconChip, Link } from '@app/core';
import { ReactNode, useCallback } from 'react';
import { useDismissInformationCards } from '@app/api';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    width: '100%',
    padding: theme.marginBase * 2,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
    marginBottom: theme.marginBase * 2,
    borderRadius: theme.borderRadius.std,
  },
  link: {
    padding: 0,
    ...theme.fonts.label,
  },
  text: {
    ...theme.fonts.caption,
    whiteSpace: 'pre-line',
  },
  buttons: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase,
  },
  chip: {
    marginRight: theme.marginBase,
  },
}));

const useColorStyles = createUseStyles((theme: Theme) =>
  mapValues(theme.colorDefinitions, (color) => ({
    backgroundColor: color.muted,
  })),
);

const useColorButtonStyles = createUseStyles((theme: Theme) =>
  mapValues(theme.colorDefinitions, (color) => ({
    color: color.accessible,
  })),
);

interface Props {
  text: ReactNode;
  sessionId: string;
  color?: Colors;
  dismissValue?:
    | 'dismissInformationReviewCard'
    | 'dismissInformationAcceptedCard'
    | 'dismissInformationBookedCard'
    | 'dismissInformationRatingCard';
  hidden?: boolean;
  buttons?: { href: string; label: string }[];
  icon?: string;
  iconColor?: Colors;
}

export const MonitoringInformationCard = ({
  text,
  sessionId,
  color = 'blue',
  dismissValue,
  hidden,
  buttons,
  icon,
  iconColor,
}: Props) => {
  const classes = useStyles();
  const colorStyles = useColorStyles();
  const colorButtonStyles = useColorButtonStyles();

  const { mutate: dismissInformationCard } =
    useDismissInformationCards(sessionId);

  const onDismiss = useCallback(() => {
    if (dismissValue) {
      dismissInformationCard({ [dismissValue]: true });
    }
  }, [dismissInformationCard, dismissValue]);

  if (hidden) {
    return null;
  }

  return (
    <div className={classnames(classes.container, colorStyles[color])}>
      <div className={classes.text}>
        {icon && (
          <IconChip icon={icon} color={iconColor} className={classes.chip} />
        )}
        {text}
      </div>
      {(buttons?.length || dismissValue) && (
        <div className={classes.buttons}>
          {buttons?.map(({ href, label }) => (
            <Link
              href={href}
              className={classnames(classes.link, colorButtonStyles[color])}
              rel="noopener noreferrer"
              target="_blank"
              arrowRight
            >
              {label}
            </Link>
          ))}
          {dismissValue && (
            <Link
              className={classnames(classes.link, colorButtonStyles[color])}
              onClick={onDismiss}
            >
              <Trans i18nKey="sessions.details.monitoring.informationCard.dismiss"></Trans>
            </Link>
          )}
        </div>
      )}
    </div>
  );
};
